// Shared Tubeframe-Joint field state (Land → Tubeframe Joint pilot).
//
// The tubeframe params were owned as local component state inside LandContent.
// The feature-tree-windows pilot (#feature-tree-windows) splits that single
// pane into TWO floating windows — "Dimensions" and "Joint" — which render in
// the VIEWPORT (ParametricPreviewSection), a different subtree from the tree
// where LandContent lives. To keep ONE source of truth across both windows (and
// the legacy single pane), the field values live in a tiny module-level store,
// subscribed via useSyncExternalStore — the same shape as useFeatureWindows.
//
// This does NOT change flag-OFF behavior: LandContent still reads/writes these
// fields and publishes readiness to homepage-context exactly as before; the only
// difference is where the state is stored. The two split panels read the same
// store, so editing "O.D." in the Dimensions window and "Joint Angle" in the
// Joint window drive the same params.

import { useCallback, useSyncExternalStore } from 'react';

export interface TubeframeFields {
  units: 'in' | 'mm';
  od: string;
  id: string;
  jointAngle: string;
  matingSurface: string;
}

const INITIAL: TubeframeFields = {
  units: 'in',
  od: '',
  id: '',
  jointAngle: '',
  matingSurface: '',
};

let state: TubeframeFields = { ...INITIAL };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

function setField<K extends keyof TubeframeFields>(
  key: K,
  value: TubeframeFields[K],
) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  emit();
}

/** Reset to blank — called when the Land content unmounts, so a module-level
 *  store doesn't retain stale field values across navigation / conversations
 *  (matches the old LandContent useState that reset on unmount). */
export function resetTubeframeFields() {
  state = { ...INITIAL };
  emit();
}

export interface TubeframeFieldsApi extends TubeframeFields {
  setField: <K extends keyof TubeframeFields>(
    key: K,
    value: TubeframeFields[K],
  ) => void;
  /** True once every field required for generation has a value. */
  ready: boolean;
}

export function useTubeframeFields(): TubeframeFieldsApi {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const ready =
    snapshot.od.trim() !== '' &&
    snapshot.id.trim() !== '' &&
    snapshot.jointAngle.trim() !== '' &&
    snapshot.matingSurface !== '';
  return {
    ...snapshot,
    ready,
    setField: useCallback(setField, []),
  };
}
